'use client';

import { c as _c } from "react/compiler-runtime";
import { MonitorIcon, MoonIcon, SunIcon } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useMounted } from '@/hooks/use-mounted';
import { getLocaleDir } from '@/i18n/locale';
import type { Locale } from '@/i18n/routing';
const themeSelectorClassName = 'flex size-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50';
export function ThemeSelector() {
  const $ = _c(44);
  const t = useTranslations("global.color_mode");
  const locale = useLocale() as Locale;
  let t0;
  if ($[0] !== locale) {
    t0 = getLocaleDir(locale);
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const dir = t0;
  const {
    theme,
    setTheme
  } = useTheme();
  const {
    isMounted
  } = useMounted();
  if (!isMounted) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className={themeSelectorClassName} />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== theme) {
    t1 = theme === "system" ? <MonitorIcon className="size-4" /> : theme === "dark" ? <MoonIcon className="size-4" /> : <SunIcon className="size-4" />;
    $[3] = theme;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t) {
    t2 = t("title");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <span className="sr-only">{t2}</span>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t3) {
    t4 = <DropdownMenuTrigger id="tour-theme-selector" className={themeSelectorClassName}>{t1}{t3}</DropdownMenuTrigger>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== setTheme) {
    t5 = () => setTheme("system");
    $[12] = setTheme;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <MonitorIcon className="mr-2 size-4" />;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("mode_system");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t7) {
    t8 = <DropdownMenuItem onClick={t5}>{t6}{t7}</DropdownMenuItem>;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== setTheme) {
    t9 = () => setTheme("light");
    $[20] = setTheme;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SunIcon className="mr-2 size-4" />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t) {
    t11 = t("mode_light");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t9) {
    t12 = <DropdownMenuItem onClick={t9}>{t10}{t11}</DropdownMenuItem>;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== setTheme) {
    t13 = () => setTheme("dark");
    $[28] = setTheme;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <MoonIcon className="mr-2 size-4" />;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t) {
    t15 = t("mode_dark");
    $[31] = t;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t13 || $[34] !== t15) {
    t16 = <DropdownMenuItem onClick={t13}>{t14}{t15}</DropdownMenuItem>;
    $[33] = t13;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t12 || $[37] !== t16 || $[38] !== t8) {
    t17 = <DropdownMenuContent>{t8}{t12}{t16}</DropdownMenuContent>;
    $[36] = t12;
    $[37] = t16;
    $[38] = t8;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== dir || $[41] !== t17 || $[42] !== t4) {
    t18 = <DropdownMenu dir={dir}>{t4}{t17}</DropdownMenu>;
    $[40] = dir;
    $[41] = t17;
    $[42] = t4;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
}